import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Tag, Toast, ActionsMenu, useConfirm } from '@pletly/ui';
import { getName } from '@pletly/shared';
import { useUser } from '@pletly/feature-auth';

import { useGetRole } from '../../../../helpers/useGetRole';
import { withTranslation } from '../../../../locales/hoc';
import { OrganisationMemberDataType, OrganisationMembersTable } from '../OrganisationMembersTable';
import { EmailCell } from '../EmailCell';
import { useRestoreMemberMutation } from './__generated__/restoreOrganisationMember.mutation.generated';
import { GetOrganisationDocument } from '../__generated__/organisation.generated';
import { UsersContainer } from '../UsersContainer';

type Props = { data: OrganisationMemberDataType[] };

export const ArchivedTable = withTranslation(({ data }: Props) => {
	const { t } = useTranslation();
	const user = useUser();
	const { getRoleString } = useGetRole();
	const [restoreMember] = useRestoreMemberMutation({
		refetchQueries: [GetOrganisationDocument],
	});
	const { confirm } = useConfirm();

	const handleRestore = useCallback(
		async ({
			memberId,
			...names
		}: {
			memberId: string;
		} & Parameters<typeof getName>[0]) => {
			const confirmation = await confirm(
				t('confirmations.unarchive-organisation-member.title'),
				t('confirmations.unarchive-organisation-member.content', {
					name: getName(names),
				}),
			);
			if (!confirmation) {
				return;
			}
			try {
				await restoreMember({
					variables: {
						id: memberId,
					},
				});
				Toast.show({
					type: 'success',
					text1: t('toasts.success.member-restored'),
				});
			} catch {
				Toast.show({
					type: 'error',
					text1: t('toasts.error.something-wrong'),
				});
			}
		},
		[t, restoreMember, confirm],
	);

	const columnsConfig = useMemo(
		() => [
			{
				name: t('table.header.username'),
				accessor: 'username' as const,
			},
			{
				name: t('table.header.role'),
				accessor: 'organisationRole' as const,
				renderCell: ({ row }: { row: Props['data'][0] }) => (
					<Tag text={getRoleString(row.role, row.organisationRole)} size="small" />
				),
			},
			{
				name: t('table.header.email'),
				accessor: 'email' as const,
				renderCell: ({ row }: { row: Props['data'][0] }) => <EmailCell email={row.email} />,
			},
		],
		[getRoleString, t],
	);

	const renderRowActions = useCallback(
		({ row }: { row: OrganisationMemberDataType }) => (
			<ActionsMenu
				actions={[
					{
						title: t('table.actions.unarchive'),
						onPress: () => handleRestore(row),
						hidden: row.id === user?.id,
					},
				]}
			/>
		),
		[handleRestore, t, user?.id],
	);

	return (
		<UsersContainer>
			<OrganisationMembersTable
				data={data}
				columnsConfig={columnsConfig}
				renderRowActions={renderRowActions}
			/>
		</UsersContainer>
	);
});
